/**
 * PrivacyPage — Política de privacidad pública (/privacidad).
 *
 * Spec: wisest-labs/02_plan-mejoras-landing.md §3.5 (D-5). Datos del
 * responsable entregados por el usuario el 2026-10-21. Base legal: Ley 19.628
 * (vigente), Ley 21.719 (vigencia 2026-12-01), Ley 20.584 + Decreto 41/2012
 * MINSAL (ficha clínica), Código Tributario (documentos tributarios).
 *
 * Reusa Header + Footer (mismo patrón que NotFoundPage). Tokens Contexto A.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import { openConsentBanner } from '../lib/consent';
import './PrivacyPage.css';

const UPDATED_AT = '2 de noviembre de 2026';
const CONTACT_EMAIL = 'conectemoscl@gmail.com';

const SECTIONS = [
  { id: 'responsable', title: 'Quién es responsable de tus datos' },
  { id: 'datos', title: 'Qué datos recopilamos' },
  { id: 'finalidad', title: 'Para qué los usamos' },
  { id: 'base-legal', title: 'Base legal del tratamiento' },
  { id: 'terceros', title: 'Con quién los compartimos' },
  { id: 'conservacion', title: 'Cuánto tiempo los guardamos' },
  { id: 'seguridad', title: 'Cómo los protegemos' },
  { id: 'derechos', title: 'Tus derechos y cómo ejercerlos' },
  { id: 'cookies', title: 'Cookies y medición' },
  { id: 'cambios', title: 'Cambios a esta política' },
];

const PrivacyPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Política de privacidad | Conectemos Chile</title>
        <meta
          name="description"
          content="Cómo Conectemos SpA recopila, usa, protege y conserva tus datos personales y de salud, y cómo ejercer tus derechos de acceso, rectificación, supresión, oposición y portabilidad."
        />
        <link rel="canonical" href="https://www.conectemoschile.cl/privacidad" />
      </Helmet>

      <Header />

      <main id="main-content" className="privacy-page">
        <header className="privacy-hero">
          <div className="container privacy-hero__inner">
            <span className="privacy-hero__eyebrow">
              <i className="fas fa-shield-alt" aria-hidden="true"></i> Privacidad y confidencialidad
            </span>
            <h1>Política de privacidad</h1>
            <p className="privacy-hero__lead">
              Lo que nos cuentas es confidencial. Esta política explica, en simple, qué datos
              tratamos, para qué, por cuánto tiempo y cómo puedes controlarlos.
            </p>
            <p className="privacy-hero__meta">Última actualización: {UPDATED_AT}</p>
          </div>
        </header>

        <div className="container privacy-layout">
          <nav className="privacy-toc" aria-label="Contenido de la política">
            <p className="privacy-toc__title">Contenido</p>
            <ol>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.title}</a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="privacy-content">
            <aside className="privacy-summary" aria-label="Resumen">
              <h2>En resumen</h2>
              <ul>
                <li><i className="fas fa-check" aria-hidden="true"></i><span>Solo pedimos los datos necesarios para contactarte y atenderte.</span></li>
                <li><i className="fas fa-check" aria-hidden="true"></i><span>Tu información clínica está protegida por secreto profesional y nunca se vende ni se usa para publicidad.</span></li>
                <li><i className="fas fa-check" aria-hidden="true"></i><span>Guardamos tus datos el tiempo que exige la ley: ni más, ni menos.</span></li>
                <li><i className="fas fa-check" aria-hidden="true"></i><span>Puedes pedir acceso, corrección o eliminación escribiendo a <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</span></li>
              </ul>
            </aside>

            <section id="responsable" className="privacy-section">
              <h2>1. Quién es responsable de tus datos</h2>
              <dl className="privacy-facts">
                <div><dt>Razón social</dt><dd>Conectemos SpA</dd></div>
                <div><dt>RUT</dt><dd>77.528.735-7</dd></div>
                <div><dt>Domicilio</dt><dd>Av. Pedro de Valdivia 273, oficina 1404, Providencia, Santiago</dd></div>
                <div><dt>Contacto de privacidad</dt><dd><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></dd></div>
              </dl>
              <p>
                Conectemos SpA, que opera bajo la marca <strong>Conectemos Chile</strong>, es responsable del
                tratamiento de los datos personales que se recogen en este sitio y en la atención psicológica
                que prestan sus profesionales.
              </p>
            </section>

            <section id="datos" className="privacy-section">
              <h2>2. Qué datos recopilamos</h2>
              <ul>
                <li>
                  <strong>Formulario de contacto:</strong> nombre, celular, correo electrónico (opcional) y el
                  servicio que te interesa. No te pedimos que nos cuentes tu motivo de consulta por este medio.
                </li>
                <li>
                  <strong>Agendamiento de horas:</strong> nombre, RUT, correo, teléfono y los datos necesarios
                  para la modalidad elegida (por ejemplo, la dirección en la atención a domicilio).
                </li>
                <li>
                  <strong>Atención psicológica:</strong> la información clínica que registra tu profesional en
                  la ficha clínica. Son <strong>datos sensibles de salud</strong> y tienen protección reforzada.
                </li>
                <li>
                  <strong>Pagos:</strong> monto, fecha, medio de pago y datos de la boleta emitida. No guardamos
                  números de tarjetas.
                </li>
                <li>
                  <strong>Navegación:</strong> métricas agregadas y anónimas de visitas, rendimiento y clics en botones de contacto.
                </li>
              </ul>
            </section>

            <section id="finalidad" className="privacy-section">
              <h2>3. Para qué los usamos</h2>
              <ul>
                <li>Responder tu solicitud de contacto y coordinar tu primera sesión.</li>
                <li>Agendar, confirmar, reagendar o cancelar tus horas y enviarte los recordatorios asociados.</li>
                <li>Prestar la atención psicológica y llevar la ficha clínica que exige la ley.</li>
                <li>Emitir boletas y cumplir obligaciones tributarias y contables.</li>
                <li>Mejorar el funcionamiento del sitio con estadísticas que no te identifican.</li>
              </ul>
              <p>
                <strong>No vendemos ni cedemos tus datos</strong>, y no usamos tu información clínica para
                publicidad ni para ningún fin distinto de tu atención.
              </p>
            </section>

            <section id="base-legal" className="privacy-section">
              <h2>4. Base legal del tratamiento</h2>
              <p>
                Tratamos tus datos conforme a la Ley N° 19.628 sobre protección de la vida privada y, desde su
                entrada en vigencia, a la Ley N° 21.719; y a la Ley N° 20.584 sobre derechos y deberes de los
                pacientes en lo relativo a la ficha clínica. Las bases son:
              </p>
              <ul>
                <li><strong>Tu consentimiento</strong>, al enviar el formulario o agendar una hora.</li>
                <li><strong>La ejecución de la atención</strong> que solicitas.</li>
                <li><strong>El cumplimiento de obligaciones legales</strong>: ficha clínica y documentos tributarios.</li>
              </ul>
              <p>Puedes retirar tu consentimiento en cualquier momento, sin efecto retroactivo.</p>
            </section>

            <section id="terceros" className="privacy-section">
              <h2>5. Con quién los compartimos</h2>
              <p>
                Solo con proveedores tecnológicos que nos prestan servicios y que tratan los datos por encargo
                nuestro, con obligaciones de confidencialidad y seguridad:
              </p>
              <ul>
                <li><strong>Supabase</strong>: base de datos y autenticación.</li>
                <li><strong>Vercel</strong>: alojamiento del sitio y métricas anónimas de uso.</li>
                <li><strong>Google</strong>: tipografías del sitio (Google Fonts) y mapa de ubicación; recibe la dirección IP del visitante.</li>
                <li><strong>Elfsight</strong>: muestra las reseñas públicas de Google en el sitio.</li>
                <li><strong>Google</strong> (Google Analytics): estadísticas de visitas al sitio, con las mismas restricciones que Meta Pixel. Solo se activa si lo aceptas, únicamente en las páginas públicas, y sin Google Signals ni personalización de anuncios.</li>
                <li><strong>Meta Platforms</strong> (Meta Pixel): medición de nuestras campañas publicitarias en Facebook e Instagram. Solo se activa en las páginas públicas (inicio, perfiles, blog, agendamiento y esta política), nunca en el portal, pagos ni activación de cuenta, y no recibe lo que escribes en los formularios.</li>
                <li><strong>Resend</strong> y <strong>Google</strong> (correo electrónico): envío y recepción de mensajes y confirmaciones.</li>
              </ul>
              <p>
                Algunos de estos proveedores almacenan información en servidores fuera de Chile. En esos casos
                exigimos niveles de protección adecuados. También podremos entregar datos cuando una autoridad
                competente lo requiera conforme a la ley.
              </p>
            </section>

            <section id="conservacion" className="privacy-section">
              <h2>6. Cuánto tiempo los guardamos</h2>
              <p>Conservamos cada dato solo por el plazo que corresponde legalmente:</p>
              <div className="privacy-table-wrap">
                <table className="privacy-table">
                  <thead>
                    <tr><th scope="col">Dato</th><th scope="col">Plazo</th><th scope="col">Fundamento</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Ficha clínica</td>
                      <td>Al menos 15 años desde la última atención</td>
                      <td>Ley N° 20.584 y su reglamento (Decreto N° 41/2012 MINSAL)</td>
                    </tr>
                    <tr>
                      <td>Boletas y registros de pago</td>
                      <td>6 años</td>
                      <td>Código Tributario</td>
                    </tr>
                    <tr>
                      <td>Datos de agendamiento</td>
                      <td>Mientras seas paciente y luego junto a tu ficha clínica</td>
                      <td>Ejecución de la atención y obligación legal</td>
                    </tr>
                    <tr>
                      <td>Formulario de contacto (si no agendas)</td>
                      <td>Solo el tiempo necesario para responder tu solicitud; luego se eliminan</td>
                      <td>Tu consentimiento</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>Cumplidos esos plazos, los datos se eliminan o se anonimizan de forma segura.</p>
            </section>

            <section id="seguridad" className="privacy-section">
              <h2>7. Cómo los protegemos</h2>
              <ul>
                <li>Conexión cifrada (HTTPS) en todo el sitio.</li>
                <li>Acceso a la información clínica restringido al profesional tratante y al personal autorizado, con control de permisos por usuario.</li>
                <li>Bitácora de acciones del sistema y registro de cambios en notas clínicas.</li>
                <li>Secreto profesional de todos nuestros psicólogos.</li>
              </ul>
            </section>

            <section id="derechos" className="privacy-section">
              <h2>8. Tus derechos y cómo ejercerlos</h2>
              <p>Respecto de tus datos personales, puedes solicitar:</p>
              <ul className="privacy-rights">
                <li><strong>Acceso</strong>: saber qué datos tenemos y cómo los usamos.</li>
                <li><strong>Rectificación</strong>: corregir datos inexactos o incompletos.</li>
                <li><strong>Supresión</strong>: eliminar tus datos, salvo los que la ley nos obliga a conservar (como la ficha clínica).</li>
                <li><strong>Oposición</strong>: oponerte a un tratamiento determinado.</li>
                <li><strong>Portabilidad</strong>: recibir tus datos en un formato de uso común.</li>
                <li><strong>Bloqueo</strong>: suspender temporalmente el tratamiento mientras se resuelve una solicitud.</li>
              </ul>
              <p>
                Escríbenos a <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> indicando tu nombre, RUT y
                qué derecho quieres ejercer. Para proteger tu información, podremos pedirte verificar tu
                identidad. Responderemos dentro de los plazos que establece la ley. El ejercicio de estos derechos es gratuito.
              </p>
              <p>
                Si consideras que no atendimos adecuadamente tu solicitud, puedes recurrir a la autoridad de
                protección de datos personales competente.
              </p>
            </section>

            <section id="cookies" className="privacy-section">
              <h2>9. Cookies y medición</h2>
              <p>
                Usamos métricas de Vercel que miden visitas, rendimiento y clics en botones de contacto de forma
                agregada, sin identificarte y sin cookies. Solo si lo aceptas, en las páginas públicas usamos además
                Google Analytics, que guarda cookies (<code>_ga</code>) y registra qué páginas visitas para entender
                cómo se usa el sitio, y Meta Pixel, que guarda una cookie (<code>_fbp</code>) y registra qué páginas
                visitas para medir nuestras campañas en Facebook e Instagram. Ninguno envía datos de formularios ni de
                tu atención clínica. Puedes aceptarlos o rechazarlos cuando quieras en{' '}
                <button type="button" className="cc-consent-reopen" onClick={openConsentBanner}>
                  Configurar cookies
                </button>
                . Puedes gestionar los anuncios que ves en la{' '}
                <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer">configuración de anuncios de Meta</a>.
                Algunas secciones incluyen contenido de terceros (mapa de Google y
                reseñas de Google mostradas mediante Elfsight), que pueden usar sus propias cookies según sus políticas.
              </p>
            </section>

            <section id="cambios" className="privacy-section">
              <h2>10. Cambios a esta política</h2>
              <p>
                Podemos actualizar esta política para reflejar cambios legales o en nuestros servicios. La
                versión vigente es siempre la publicada en esta página, con su fecha de actualización.
              </p>
            </section>

            <div className="privacy-cta">
              <p>¿Tienes dudas sobre tus datos?</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="privacy-cta__btn">
                <i className="fas fa-envelope" aria-hidden="true"></i> Escríbenos
              </a>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default PrivacyPage;
